import { Storage } from '@ionic/storage-angular';
import { IConfigService, IModelEntity, ILoggingService, UtilsMisc, UtilsString } from '@pit/core';
import { LocalStorageProviderBase } from './local.storage.provider.base';

export class LocalStorageProvider<T extends IModelEntity> extends LocalStorageProviderBase<T> {

    constructor(configurationService: IConfigService, loggingService: ILoggingService, entityName: string, protected storage: Storage) {
        super(configurationService, loggingService, entityName);
    }

    protected override doClear(): void {
        this.clearAsync();
    }

    protected override async doGetIdExistsAsync(id: number): Promise<boolean> {
        let result = false;

        const keys: string[] = await this.storage.keys();
        if ((UtilsMisc.isNullOrUndefined(keys) === false) && (keys.length > 0)) {
            result = (keys.indexOf(this.getStorageKey(id)) >= 0);
        }

        return result;
    }

    protected override async doGetCountAsync(): Promise<number> {
        let result = 0;
        const keys: string[] = (await this.storage.keys()).filter(key => key.startsWith(this.entityName));
        if ((UtilsMisc.isNullOrUndefined(keys) === false) && (keys.length > 0)) {
            result = keys.length;
        }

        return result;
    }

    protected override async doClearAsync(): Promise<void> {
        const keys: string[] = (await this.storage.keys()).filter(key => key.startsWith(this.entityName));
        if ((UtilsMisc.isNullOrUndefined(keys) === false) && (keys.length > 0)) {
            for (let loop = 0; loop < keys.length; loop++) {
                await this.storage.remove(keys[loop]);
            }
        }
    }

    protected override async doDeleteAsync(entity: T): Promise<T> {
        await this.storage.remove(this.getStorageKey(entity.id));

        return entity;
    }

    protected override async doGetAllAsync(): Promise<T[]> {
        const result: T[] = [];
        const keys: string[] = (await this.storage.keys()).filter(key => key.startsWith(this.entityName));
        if ((UtilsMisc.isNullOrUndefined(keys) === false) && (keys.length > 0)) {
            for (let loop = 0; loop < keys.length; loop++) {
                const data: any = this.decryptData(await this.storage.get(keys[loop]));
                result.push(this.createEntityFromJson(data));
            }
        }

        return result;
    }

    protected override async doGetByIdAsync(id: number): Promise<T> {
        let result: T = null;
        const data: any = await this.storage.get(this.getStorageKey(id));
        if (UtilsMisc.isNullOrUndefined(data) === false) {
            result = this.createEntityFromJson(this.decryptData(data));
        } else {
            result = null;
        }

        return result;
    }

    protected override async doGetElementAtAsync(index: number): Promise<T> {
        let result: T = null;
        const keys: string[] = (await this.storage.keys()).filter(key => key.startsWith(this.entityName));
        if ((UtilsMisc.isNullOrUndefined(keys) === false) && (keys.length > 0)) {
            if ((index >= 0) && (index < keys.length)) {
                const data: any = await this.storage.get(keys[index]);
                if (UtilsMisc.isNullOrUndefined(data) === false) {
                    result = this.createEntityFromJson(this.decryptData(data));
                } else {
                    result = null;
                }
            }
        }

        return result;
    }

    protected override async doSaveAsync(entity: T): Promise<T> {
        const entityToSave: T = (entity.clone() as T);
        entityToSave.removeReferences();

        //await this.storage.set(this.getStorageKey(entityToSave.id), entityToSave);
        await this.storage.set(this.getStorageKey(entityToSave.id), this.encryptData(entityToSave));

        return entity;
    }

    protected override async doSaveMultiAsync(entities: T[]): Promise<void> {
        if ((entities != null) && (entities.length > 0)) {
            for (let loop = 0; loop < entities.length; loop++) {
                await this.saveAsync(entities[loop]);
            }
        }
    }

    protected override doSyncSavedItemsToRemote(): boolean {
        return false;
    }

    protected override async doSyncSavedItemsToRemoteAsync(): Promise<boolean> {
        return false;
    }

    protected override createEntityFromJson(json: any): T {
        return null;
    }

    protected override getStorageKey(id: number): string {
        return UtilsString.stringFormat('{0}->[{1}]', this.entityName, id);
    }

}
